import { Hl7Context, Hl7Segment } from '@medplum/core';
import { Decimal } from 'decimal.js';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { and, assign, not, or, setup } from 'xstate';
import * as z from 'zod';
import { ENV } from '../../env.js';
import { AdministrationMode, CompoundOrderStatus, DoseCalcModeActive, PreparationType, OrderBatchCategory, PatientTherapySequenceStatus, ProductionWeighingStep, RestVialStatus, ReturnReason, ReturnStatus, StockMovementSource, StockMovementType, VolumeMode, } from '../../database/schema.js';
import { AMMS_MSH_DEFAULTS, buildAmmsObrSegment, buildAmmsObxSegment, buildAmmsOrcSegment, buildAmmsPidSegment, buildAmmsPv1Segment, buildAmmsRqdSegment, buildAmmsZcmSegment, buildAmmsZivFinishedPreparationSegment, computeAmmsConsumptionLines, computeAmmsPackageContent, createAmmsMessage, deriveAmmsPrescriptionId, formatAmmsDate, formatAmmsPznOrEan, getAmmsProductionSkipReason, MESSAGE_TYPES, } from '../../hl7/message.js';
import { ListItemSchema, schema, zql } from '../../schema.js';
import { CarrierSolutionValidationSchema, therapySequenceIncludesActiveSubstance } from '../../util/patients-therapies.js';
import { countPriorAmmsLogs, logAmmsOutboundMessage, nextAmmsControlSequence, resolveAmmsOrcProvider, } from '../utils/amms.js';
import { EventLogs, getDosingWeight, getTotalAmountOfSubstanceDecimal, hasPermission, isDefined, } from '../../utils.js';
import { addEventLog, EventLogResources } from '../util.js';
import { recordStockMovement } from '../utils/stock-movements.js';
import { changedItems, itemsDetail, valueItem } from '../../util/event-log-items.js';
import { validationItems } from '../patient/medicine-log-items.js';
import { pickingSourceItems } from './production-log-items.js';
import { nameOf } from '../utils/log-names.js';
import { lockRestVials } from '../utils/rest-vials.js';
import { isReturnExpired } from '../utils/return-expiry.js';
export const PatientTherapySequenceEventType = z.enum([
    'APPROVE_BUDGET',
    'REJECT_BUDGET',
    'DOCTOR_VALIDATE',
    'PHARMACIST_VALIDATE',
    // The patient-arrival step only applies while the `doctorRelease` setting is true.
    'PATIENT_ARRIVED',
    'REVERSE_PATIENT_ARRIVED',
    'RELEASE_TO_PRODUCTION',
    'START_PRODUCTION',
    'COMPLETE_PRODUCTION',
    'CANCEL_PRODUCTION',
    'RELEASE_FOR_DELIVERY',
    'ADMINISTER',
    'RETURN_TO_PHARMACY',
    //   'RE_RELEASE',
    'SPOILAGE',
    'REVISE',
]);
/** The permission each prescription event needs, checked before the state machine runs any side effect. */
export const SequenceEventPermissions = {
    APPROVE_BUDGET: 'prescriptions.approve_budget',
    REJECT_BUDGET: 'prescriptions.approve_budget',
    DOCTOR_VALIDATE: 'prescriptions.validate_doctor',
    PHARMACIST_VALIDATE: 'prescriptions.validate_pharmacist',
    PATIENT_ARRIVED: 'prescriptions.patient_arrival',
    REVERSE_PATIENT_ARRIVED: 'prescriptions.patient_arrival',
    RELEASE_TO_PRODUCTION: 'production.produce',
    START_PRODUCTION: 'production.produce',
    COMPLETE_PRODUCTION: 'production.produce',
    CANCEL_PRODUCTION: 'production.produce',
    RELEASE_FOR_DELIVERY: 'production.release',
    // NURSE_VERIFY: 'administration.administer',
    ADMINISTER: 'administration.administer',
    RETURN_TO_PHARMACY: 'administration.administer',
    SPOILAGE: 'production.release',
    REVISE: 'prescriptions.edit',
};
const ValidationEventCommonFields = z.object({
    applicationFormId: z.string().optional(),
    carrierSolutionId: z.string().optional(),
    duration: z.number().optional(),
    volume: z.number().optional(),
    volumeMode: VolumeMode.optional(),
    therapyNote: z.string().optional(),
    pharmacyNote: z.string().optional(),
});
const ValidationMedicineBase = z.object({
    id: z.string(),
    prescribedNameId: z.string().optional(),
});
export const PharmacistValidateEventBody = ValidationEventCommonFields.extend({
    preparationType: PreparationType.optional(),
    medicine: z.array(ValidationMedicineBase),
});
export const ReturnToPharmacyEventBody = z.object({
    reason: ReturnReason,
    stockLocationId: z.string().nonempty(),
    note: z.string().optional(),
});
export const DispatchMode = z.enum(['manual', 'smart-compounder']);
export const StartProductionEventBody = z.object({
    sequenceIds: z.array(z.string()),
    orders: z.array(ListItemSchema),
    initialOrders: z.array(ListItemSchema).optional(),
    productionLocationId: z.string(),
    usedBatches: z.array(z.string()),
    dispatchMode: DispatchMode.default(DispatchMode.enum.manual),
});
export const UsedBatches = z.object({
    batchId: z.string(),
    quantity: z.number(),
});
export const AdministerEventBody = z.object({
    completed: z.boolean().optional(),
    /** The Administration mode used (Settings > Workflow), saved on the prescription. */
    mode: AdministrationMode.optional(),
    /** Scan mode only: the nurse skipped the patient wristband scan. */
    patientScanSkipped: z.boolean().optional(),
});
// Reason for rejecting a produced unit at QA or aborting it in production.
export const AbortReason = z.enum([
    'PRESCRIPTION_ERROR',
    'TECHNICAL_ERROR',
    'BREAKAGE_CONTAMINATION',
    'PATIENT_STATUS_CHANGED',
    'SCALE_ERROR',
]);
// Reason for rejecting a released preparation at QA & dispatch (CY-340); OTHER needs a note.
export const QaRejectReason = z.enum([
    'LABEL_ERROR',
    'LEAKAGE',
    'PARTICLES_DISCOLOURATION',
    'WRONG_VOLUME_WEIGHT',
    'EXPIRED',
    'OTHER',
]);
export const SpoilageEventBody = z
    .object({
    remake: z.boolean(),
    reason: QaRejectReason,
    note: z.string().trim().max(500).optional(),
    // the Available rest vials the pharmacist ticked to discard; the others are kept (CY-329)
    restVialIds: z.array(z.uuidv7()),
})
    .refine(body => body.reason !== QaRejectReason.enum.OTHER || Boolean(body.note), {
    path: ['note'],
    message: 'note_required',
});
export const ProductionWeighing = z.object({
    step: ProductionWeighingStep,
    substanceIndex: z.int().min(0),
    medicineId: z.string().nullable(),
    allocatedDose: z.number().nullable(),
    expectedWeightGrams: z.number().nullable(),
    actualWeightGrams: z.number(),
    recordedAt: z.number(),
    batchId: z.string().nullable().optional(),
    scaleMemoryNumber: z.int().nullable().optional(),
    /** user who took the reading; another user may resume and complete the preparation */
    recordedBy: z.string().nullable().optional(),
});
export const CompleteProductionEventBody = z.object({ weighings: z.array(ProductionWeighing) });
export const PatientTherapySequenceEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.APPROVE_BUDGET) }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.REJECT_BUDGET) }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.DOCTOR_VALIDATE) }),
    z.object({
        type: z.literal(PatientTherapySequenceEventType.enum.PHARMACIST_VALIDATE),
        body: PharmacistValidateEventBody,
    }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.PATIENT_ARRIVED) }),
    // empty body marks a confirmed reverse; without it the confirmation dialog opens
    z.object({
        type: z.literal(PatientTherapySequenceEventType.enum.REVERSE_PATIENT_ARRIVED),
        body: z.object({}).optional(),
    }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.RELEASE_TO_PRODUCTION) }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.START_PRODUCTION), body: StartProductionEventBody }),
    // the cleanroom sends its weighings; completing from the drawer sends no body
    z.object({
        type: z.literal(PatientTherapySequenceEventType.enum.COMPLETE_PRODUCTION),
        body: CompleteProductionEventBody.optional(),
    }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.CANCEL_PRODUCTION) }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.RELEASE_FOR_DELIVERY) }),
    z.object({
        type: z.literal(PatientTherapySequenceEventType.enum.RETURN_TO_PHARMACY),
        body: ReturnToPharmacyEventBody,
    }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.ADMINISTER), body: AdministerEventBody.optional() }),
    // z.object({type: z.literal(PatientTherapySequenceEventType.enum.RE_RELEASE)}),
    z.object({
        type: z.literal(PatientTherapySequenceEventType.enum.SPOILAGE),
        body: SpoilageEventBody,
    }),
    z.object({ type: z.literal(PatientTherapySequenceEventType.enum.REVISE) }),
]);
const patientTherapySequenceMachine = setup({
    types: {
        context: {},
        input: {},
        events: {},
    },
    guards: {
        canApproveBudget: ({ context }) => hasPermission(context.user?.groups, 'prescriptions.approve_budget'),
        canValidateDoctor: ({ context }) => hasPermission(context.user?.groups, 'prescriptions.validate_doctor'),
        canValidatePharmacist: ({ context }) => hasPermission(context.user?.groups, 'prescriptions.validate_pharmacist'),
        canEditPrescription: ({ context }) => hasPermission(context.user?.groups, 'prescriptions.edit'),
        canMarkPatientArrival: ({ context }) => hasPermission(context.user?.groups, 'prescriptions.patient_arrival'),
        canProduce: ({ context }) => hasPermission(context.user?.groups, 'production.produce'),
        canRelease: ({ context }) => hasPermission(context.user?.groups, 'production.release'),
        canAdminister: ({ context }) => hasPermission(context.user?.groups, 'administration.administer'),
        isPharmaValidated: ({ context }) => Boolean(context.sequence?.pharmacistValidatedAt),
        isPatientArrived: ({ context }) => Boolean(context.sequence?.patientArrivedAt),
        // `doctorRelease` defaults to true (arrival step enabled); false skips it
        isDoctorReleaseEnabled: ({ context }) => context.settings?.doctorRelease !== false,
        isActiveSubstance: ({ context }) => therapySequenceIncludesActiveSubstance(context.sequence),
        // dispatch releases straight to `administered` when the organisation opted out of
        // the nurse administration step (private-pharmacy workflow, no PDA scan)
        isMarkAdministeredOnDispatch: ({ context }) => Boolean(context.settings?.markAdministeredOnDispatch),
        isAdministeredOnDispatch: ({ context }) => context.sequence?.administeredOnDispatch === true,
        isDeliveryAutoRelease: ({ context }) => Boolean(context.settings?.deliveryAutoRelease),
    },
    actions: {
        // clears the arrival timestamp so the machine re-enters "awaiting release", not production planning
        clearPatientArrival: assign({
            sequence: ({ context }) => (context.sequence ? { ...context.sequence, patientArrivedAt: null } : context.sequence),
        }),
    },
}).createMachine({
    id: 'patient-therapy-sequence',
    context: ({ input }) => ({ ...input }),
    initial: PatientTherapySequenceStatus.enum.pending_doctor_validation,
    states: {
        [PatientTherapySequenceStatus.enum.pending_approval]: {
            on: {
                APPROVE_BUDGET: {
                    target: PatientTherapySequenceStatus.enum.pending_doctor_validation,
                    guard: 'canApproveBudget',
                },
                REJECT_BUDGET: { target: PatientTherapySequenceStatus.enum.budget_not_approved, guard: 'canApproveBudget' },
            },
        },
        [PatientTherapySequenceStatus.enum.pending_doctor_validation]: {
            on: {
                DOCTOR_VALIDATE: {
                    target: PatientTherapySequenceStatus.enum.validating,
                    guard: 'canValidateDoctor',
                },
                // Edit/Review opens the prescription editor (UI-only); the status stays put until submitted.
                REVISE: {
                    target: PatientTherapySequenceStatus.enum.pending_doctor_validation,
                    reenter: false,
                    guard: 'canEditPrescription',
                },
            },
        },
        [PatientTherapySequenceStatus.enum.validating]: {
            type: 'parallel',
            // The doctor or the pharmacist may review/revise the prescription until the pharmacist
            // validates it. REVISE is a UI action that opens the editor — the sequence stays in `validating`.
            on: {
                REVISE: {
                    target: PatientTherapySequenceStatus.enum.validating,
                    reenter: false,
                    guard: and(['canEditPrescription', not('isPharmaValidated')]),
                },
            },
            always: [
                {
                    target: PatientTherapySequenceStatus.enum.administration,
                    guard: and([not('isActiveSubstance'), 'isPharmaValidated']),
                },
                {
                    target: PatientTherapySequenceStatus.enum.production_planning,
                    // Arrival is required only while `doctorRelease` keeps the patient-arrival step enabled.
                    guard: and([
                        'isActiveSubstance',
                        'isPharmaValidated',
                        or(['isPatientArrived', not('isDoctorReleaseEnabled')]),
                    ]),
                },
            ],
            states: {
                pharmacistCheck: {
                    initial: 'pending',
                    states: {
                        pending: {
                            on: {
                                PHARMACIST_VALIDATE: {
                                    target: 'confirmed',
                                    guard: and(['canValidatePharmacist', not('isPharmaValidated')]),
                                },
                            },
                        },
                        confirmed: { type: 'final' },
                    },
                },
                patientArrival: {
                    initial: 'pending',
                    // the same reverse as production_planning, available while the sequence is still validating
                    on: {
                        REVERSE_PATIENT_ARRIVED: {
                            target: '.pending',
                            guard: and(['isDoctorReleaseEnabled', 'canMarkPatientArrival', 'isPatientArrived']),
                            actions: 'clearPatientArrival',
                        },
                    },
                    states: {
                        pending: {
                            // Arrival is only required for active substances, and only while `doctorRelease` keeps the step enabled.
                            on: {
                                PATIENT_ARRIVED: {
                                    target: 'confirmed',
                                    guard: and([
                                        'isDoctorReleaseEnabled',
                                        'isActiveSubstance',
                                        'canMarkPatientArrival',
                                        not('isPatientArrived'),
                                    ]),
                                },
                            },
                        },
                        confirmed: { type: 'final' },
                    },
                },
            },
            onDone: [
                {
                    target: PatientTherapySequenceStatus.enum.administration,
                    guard: not('isActiveSubstance'),
                },
                {
                    target: PatientTherapySequenceStatus.enum.production_planning,
                },
            ],
        },
        [PatientTherapySequenceStatus.enum.production_planning]: {
            on: {
                START_PRODUCTION: {
                    target: PatientTherapySequenceStatus.enum.in_production,
                    guard: 'canProduce',
                },
                // Edit (doctor) / Review (pharmacist) stays available after both validations, until production starts — submitting the edit reverts the prescription to doctor validation.
                REVISE: {
                    target: PatientTherapySequenceStatus.enum.production_planning,
                    reenter: false,
                    guard: 'canEditPrescription',
                },
                // the ward may undo the arrival until production starts, keeping both validations
                REVERSE_PATIENT_ARRIVED: {
                    target: PatientTherapySequenceStatus.enum.validating,
                    guard: and(['isDoctorReleaseEnabled', 'canMarkPatientArrival', 'isPatientArrived']),
                    actions: 'clearPatientArrival',
                },
            },
        },
        [PatientTherapySequenceStatus.enum.in_production]: {
            on: {
                // Auto-release for delivery skips QA & dispatch; the release is logged as automatic (CY-263)
                COMPLETE_PRODUCTION: [
                    {
                        target: PatientTherapySequenceStatus.enum.administered,
                        guard: and(['canProduce', 'isDeliveryAutoRelease', 'isMarkAdministeredOnDispatch']),
                    },
                    {
                        target: PatientTherapySequenceStatus.enum.released_for_delivery,
                        guard: and(['canProduce', 'isDeliveryAutoRelease']),
                    },
                    {
                        target: PatientTherapySequenceStatus.enum.qa_dispatch,
                        guard: 'canProduce',
                    },
                ],
                // Cancel production reuses the cleanroom abort flow (materials + reason); the drawer intercepts the body-less event and opens the abort dialog, which cancels the sequence.
                CANCEL_PRODUCTION: {
                    target: PatientTherapySequenceStatus.enum.cancelled,
                    guard: 'canProduce',
                },
            },
        },
        [PatientTherapySequenceStatus.enum.qa_dispatch]: {
            on: {
                RELEASE_FOR_DELIVERY: [
                    {
                        target: PatientTherapySequenceStatus.enum.administered,
                        guard: and(['canRelease', 'isMarkAdministeredOnDispatch']),
                    },
                    {
                        target: PatientTherapySequenceStatus.enum.released_for_delivery,
                        guard: 'canRelease',
                    },
                ],
                SPOILAGE: { target: PatientTherapySequenceStatus.enum.discarded, guard: 'canRelease' },
            },
        },
        // No separate Nurse verify step (CY-212): Administer is offered as soon as the preparation is released.
        [PatientTherapySequenceStatus.enum.released_for_delivery]: {
            on: {
                ADMINISTER: { target: PatientTherapySequenceStatus.enum.administered, guard: 'canAdminister' },
                // NURSE_VERIFY: {
                //   target: PatientTherapySequenceStatus.enum.administration,
                //   guard: 'canAdminister',
                // },
                RETURN_TO_PHARMACY: { target: PatientTherapySequenceStatus.enum.returned, guard: 'canAdminister' },
            },
        },
        // Legacy: prescriptions verified before CY-212 can still be administered or returned.
        [PatientTherapySequenceStatus.enum.administration]: {
            on: {
                ADMINISTER: { target: PatientTherapySequenceStatus.enum.administered, guard: 'canAdminister' },
                RETURN_TO_PHARMACY: { target: PatientTherapySequenceStatus.enum.returned, guard: 'canAdminister' },
            },
        },
        // Marked Administered on dispatch, the ward can still return it as Not administered (CY-332);
        // a nurse administration stays final.
        [PatientTherapySequenceStatus.enum.administered]: {
            on: {
                RETURN_TO_PHARMACY: {
                    target: PatientTherapySequenceStatus.enum.returned,
                    guard: and(['canAdminister', 'isAdministeredOnDispatch']),
                },
            },
        },
        [PatientTherapySequenceStatus.enum.returned]: {
            // on: {
            //   RE_RELEASE: {target: PatientTherapySequenceStatus.enum.released_for_delivery, guard: 'isPharmacist'},
            // },
            type: 'final',
        },
        [PatientTherapySequenceStatus.enum.discarded]: { type: 'final' },
        [PatientTherapySequenceStatus.enum.budget_not_approved]: { type: 'final' },
        [PatientTherapySequenceStatus.enum.cancelled]: { type: 'final' },
    },
});
function buildPatientTherapySequenceMachineContext({ user, sequence, settings, }) {
    return {
        user,
        sequence,
        settings,
    };
}
function resolvePatientTherapySequenceState(payload, options) {
    if (!payload.sequence?.status)
        return null;
    const context = buildPatientTherapySequenceMachineContext(payload);
    const isActiveSubstance = therapySequenceIncludesActiveSubstance(payload.sequence);
    const isDoctorReleaseEnabled = payload.settings?.doctorRelease !== false;
    let pharmacistCheck = 'pending';
    if (payload.sequence?.pharmacistValidatedAt)
        pharmacistCheck = 'confirmed';
    let patientArrival = 'pending';
    // When `doctorRelease` is off the arrival step is skipped, so the region starts confirmed.
    if (!isActiveSubstance || !isDoctorReleaseEnabled || payload.sequence?.patientArrivedAt)
        patientArrival = 'confirmed';
    return patientTherapySequenceMachine.resolveState({
        ...options,
        context,
        value: payload.sequence.status === PatientTherapySequenceStatus.enum.validating
            ? { validating: { pharmacistCheck, patientArrival } }
            : payload.sequence.status,
    });
}
export const PatientTherapySequenceMachine = {
    instance: patientTherapySequenceMachine,
    buildContext: buildPatientTherapySequenceMachineContext,
    resolveState: resolvePatientTherapySequenceState,
    /**
     * Builds a stable React key from the resolved machine context. Components that
     * drive a therapy-sequence actor must re-mount when this key changes (the actor
     * is only hydrated from its context on creation), so it has to capture the data
     * the machine's guards and state value depend on: the current user's group types,
     * the sequence's status/pharmacist/patient-arrival fields, and the workflow settings
     * that gate them (`doctorRelease`, `markAdministeredOnDispatch`, `deliveryAutoRelease`).
     */
    buildKey(context) {
        const { user, sequence, settings } = context;
        const groupTypes = (user?.groups ?? [])
            .map(group => group.type)
            .toSorted()
            .join(',');
        return [
            sequence?.id ?? '',
            sequence?.status ?? '',
            sequence?.pharmacistValidatedAt ?? '',
            sequence?.patientArrivedAt ?? '',
            sequence?.administeredOnDispatch === true ? 'on-dispatch' : '',
            user?.id ?? '',
            groupTypes,
            settings?.markAdministeredOnDispatch === true ? 'administered-on-dispatch' : '',
            settings?.doctorRelease === false ? 'doctor-release-off' : '',
            settings?.deliveryAutoRelease === true ? 'delivery-auto-release' : '',
        ].join('.');
    },
};
function combineAmmsDateAndTime(therapyDate, time) {
    const [hours = 0, minutes = 0] = time.split(':').map(Number);
    const date = new Date(therapyDate);
    date.setHours(hours, minutes, 0, 0);
    return date;
}
/**
 * Builds the MSH-less segments shared by every outbound AMMS ORM^O01 message for a therapy
 * sequence (pharmacist validation, production completion): PID, PV1, ORC, OBR, OBX x3 and RQD.
 */
async function buildAmmsCoreSegments(tx, params) {
    const { step, sequence, medicine, pharmacistUserId, now } = params;
    const context = new Hl7Context();
    const prescriptionId = deriveAmmsPrescriptionId(sequence.internalId ?? '');
    const applicationForm = sequence.applicationFormId
        ? await tx.run(zql.applicationForms.where('id', '=', sequence.applicationFormId).one())
        : undefined;
    const route = applicationForm?.name ?? '';
    const patient = await tx.run(zql.patients.where('id', '=', sequence.patientId).one());
    const homeContact = await tx.run(zql.patientContacts
        .where('patientId', '=', sequence.patientId)
        .where('contactType', '=', 'home')
        .where('deletedAt', 'IS', null)
        .related('address')
        .one());
    const encounter = await tx.run(zql.patientEncounters
        .where('patientId', '=', sequence.patientId)
        .where('status', '=', 'active')
        .orderBy('startedAt', 'desc')
        .limit(1)
        .one());
    const ward = encounter?.wardId ? await tx.run(zql.wards.where('id', '=', encounter.wardId).one()) : undefined;
    const section = encounter?.sectionId
        ? await tx.run(zql.sections.where('id', '=', encounter.sectionId).one())
        : undefined;
    const destinationCode = ward?.code ?? section?.code ?? '';
    const [doctor, pharmacist] = await Promise.all([
        resolveAmmsOrcProvider(tx, sequence.doctorValidatedBy),
        resolveAmmsOrcProvider(tx, pharmacistUserId),
    ]);
    const orcSegment = buildAmmsOrcSegment({
        context,
        step,
        prescriptionId,
        plannedAdministrationAt: combineAmmsDateAndTime(sequence.therapyDate, sequence.time),
        generatedAt: new Date(now),
        doctor,
        pharmacist,
        destinationCode,
        createdAt: new Date(sequence.createdAt ?? now),
    });
    const pidSegment = patient ? buildAmmsPidSegment({ context, patient, contact: homeContact }) : undefined;
    // Replays the encounter's raw PV1 verbatim when available (legacy ASSECO_PV1_RECORD
    // byte-compatibility); otherwise rebuilds it from the encounter's structured fields.
    const pv1Segment = encounter
        ? encounter.rawPv1
            ? Hl7Segment.parse(encounter.rawPv1, context)
            : buildAmmsPv1Segment({ context, encounter })
        : undefined;
    const logCount = await countPriorAmmsLogs(tx, sequence.id);
    const obrSegment = buildAmmsObrSegment({ context, prescriptionId, logCount });
    // Legacy ASSECO_OBX_RECORDS: weight, height, and the configured IBW/AIBW value — reported as
    // "powierzchnia" with unit 'm' (spec Appendix C), not the body surface area.
    const characteristics = await tx.run(zql.patientCharacteristics.where('patientId', '=', sequence.patientId).orderBy('createdAt', 'desc').limit(1).one());
    const obxSegments = [
        { code: 1, name: 'masa', value: characteristics?.weight, unit: 'Kg' },
        { code: 2, name: 'wzrost', value: characteristics?.height, unit: 'cm' },
        { code: 3, name: 'powierzchnia', value: characteristics?.aibw, unit: 'm' },
    ]
        .filter((obs) => obs.value != null)
        .map(obs => buildAmmsObxSegment({ context, ...obs }));
    const medicineDetails = await Promise.all(medicine.map(async (medicineRow) => {
        const drugCatalogItem = medicineRow.activeSubstanceId
            ? await tx.run(zql.drugCatalogItems
                .where('id', '=', medicineRow.activeSubstanceId)
                .related('activeSubstanceDetails', d => d.related('doseUnit'))
                .one())
            : undefined;
        const prescribedName = await tx.run(zql.prescribedNames.where('id', '=', medicineRow.prescribedNameId).one());
        const drugName = prescribedName?.name ?? drugCatalogItem?.name ?? '';
        const doseAmount = Number(medicineRow.totalAmount ?? 0);
        const doseUnit = drugCatalogItem?.activeSubstanceDetails?.doseUnit?.displayName ?? 'mg';
        return { drugName, doseAmount, doseUnit };
    }));
    const buildRqdSegments = () => medicineDetails.map(({ drugName, doseAmount, doseUnit }) => buildAmmsRqdSegment({
        context,
        prescriptionId,
        drugName,
        doseAmount,
        doseUnit,
        route,
        durationMinutes: sequence.duration,
    }));
    const medicineNames = medicineDetails
        .map(({ drugName, doseAmount, doseUnit }) => `${drugName} ${doseAmount}${doseUnit}`.trim())
        .join(', ');
    // Shared ORM header up to and including OBX; RQD follows (and, for CM, ZIV/ZCM).
    const headerSegments = [
        ...(pidSegment ? [pidSegment] : []),
        ...(pv1Segment ? [pv1Segment] : []),
        orcSegment,
        obrSegment,
        ...obxSegments,
    ];
    return { context, headerSegments, buildRqdSegments, medicineNames };
}
/**
 * Builds and logs the outbound AMMS ORM^O01 (NW / SC_2) message — MSH/PID/PV1/ORC/OBR/OBX/RQD —
 * for a pharmacist-validated therapy sequence (spec §8). Server-only: HL7EventLog rows are
 * written directly, not synced from the client.
 */
async function logAmmsOrmMessage(tx, params) {
    if (!ENV.AMMS_SYSTEM || tx.location !== 'server')
        return;
    const { sequence, now } = params;
    const { headerSegments, buildRqdSegments, medicineNames } = await buildAmmsCoreSegments(tx, {
        ...params,
        step: 'validated',
    });
    await logAmmsOutboundMessage(tx, {
        now,
        resourceRef: [{ resource: EventLogResources.patientTherapySequence, id: sequence.id, name: medicineNames }],
        build: async () => createAmmsMessage({
            ...AMMS_MSH_DEFAULTS,
            messageType: MESSAGE_TYPES.PRESCRIPTION_VALIDATE_MESSAGE_TYPE,
            sequence: await nextAmmsControlSequence(tx),
            sentAt: new Date(now),
            segments: [...headerSegments, ...buildRqdSegments()],
        }),
    });
}
/**
 * Builds and logs the outbound AMMS ORM^O01 (OE / CM) message — MSH/PID/PV1/ORC/OBR/OBX/RQD/ZIV/ZCM —
 * for a completed production, per section 9 of the AMMS integration spec. Only active-substance
 * batches are reported. If any of them has no stored raw ZIV (it did not come from AMMS) or no
 * batch record, the report is still built without that batch's ZCM/ZIV lines but logged as
 * SKIPPED, so it is never sent or retried; this never blocks the transition. Server-only: HL7EventLog rows are written directly, not synced
 * from the client.
 */
export async function logAmmsProductionCompleteMessage(tx, params) {
    if (!ENV.AMMS_SYSTEM || tx.location !== 'server')
        return;
    const { sequence, medicine, order, now } = params;
    const { context, headerSegments, buildRqdSegments, medicineNames } = await buildAmmsCoreSegments(tx, {
        ...params,
        step: 'produced',
    });
    // The `patientTherapySequenceMedicine <-> batch` link isn't normalized; batch usage is read
    // from the order's distributed-batches JSON, grouped by sequence + category (not by medicine
    // row). Each batch is assigned to the medicine sharing its trade name's prescribed name, so a
    // multi-substance prescription reports every substance; unmatched batches go to the first one.
    const distributedItem = order.distributedOrders?.orders?.find(item => item.sequenceId === sequence.id);
    const batchUsages = distributedItem?.activeSubstances.batches ?? [];
    const resolvedBatchUsages = await Promise.all(batchUsages.map(async (usage) => ({
        usage,
        batch: await tx.run(zql.batches
            .where('id', '=', usage.batchId)
            .related('tradeName', tn => tn.related('strengthUnit'))
            .one()),
    })));
    // one DRUG weighing per vial drawn in the cleanroom, saved just before this report
    const drugWeighings = await tx.run(zql.productionDetails
        .where('therapySequenceId', '=', sequence.id)
        .where('step', '=', 'DRUG')
        .where('deletedAt', 'IS', null));
    const medicinePrescribedNameIds = new Set(medicine.map(medicineRow => medicineRow.prescribedNameId));
    const medicineGroups = await Promise.all(medicine.map(async (medicineRow, index) => {
        const drugCatalogItem = medicineRow.activeSubstanceId
            ? await tx.run(zql.drugCatalogItems
                .where('id', '=', medicineRow.activeSubstanceId)
                .related('activeSubstanceDetails', d => d.related('doseUnit'))
                .one())
            : undefined;
        const ownsBatch = ({ batch }) => {
            const prescribedNameId = batch?.tradeName?.prescribedNameId;
            return prescribedNameId && medicinePrescribedNameIds.has(prescribedNameId)
                ? prescribedNameId === medicineRow.prescribedNameId
                : index === 0;
        };
        return {
            medicineRow,
            unit: drugCatalogItem?.activeSubstanceDetails?.doseUnit?.displayName ?? 'mg',
            doseUnit: drugCatalogItem?.activeSubstanceDetails?.doseUnit?.ucumCode,
            totalPrescribedAmount: Number(medicineRow.totalAmount ?? 0),
            batchUsages: resolvedBatchUsages.filter(ownsBatch),
            weighings: drugWeighings.filter(weighing => medicine.length === 1 || !weighing.medicineId || weighing.medicineId === medicineRow.id),
        };
    }));
    const resourceRef = [{ resource: EventLogResources.patientTherapySequence, id: sequence.id, name: medicineNames }];
    // AMMS can only match batches it sent, so a batch without its raw ZIV skips the whole report. The
    // message is still built (without that batch's ZCM/ZIV lines) and kept on the SKIPPED row for review.
    const skipReason = getAmmsProductionSkipReason(resolvedBatchUsages.map(({ batch }) => batch));
    const leftOut = resolvedBatchUsages.filter(({ batch }) => !batch?.rawZiv);
    if (leftOut.length > 0) {
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: sequence.id,
            action: 'update',
            domain: 'production',
            event: EventLogs.HL7_PRODUCTION_REPORT_INCOMPLETE,
            status: 'WARNING',
            performedBy: params.pharmacistUserId,
            performedAt: now,
            details: {
                therapyInternalId: sequence.internalId ?? '',
                ...itemsDetail(leftOut.flatMap(({ batch }) => valueItem('lot', batch?.lotNumber))),
            },
        });
    }
    await logAmmsOutboundMessage(tx, {
        now,
        resourceRef,
        skipReason,
        build: async () => {
            // one ZCM/ZIV pair per vial drawn (spec §9.7), numbered across all medicines; ZCM-6 carries a
            // medicine's total on its first line only
            const consumptionSegments = [];
            for (const group of medicineGroups) {
                // amount drawn per vial in the dose unit; throws (logged FAILED) rather than report a guessed amount
                const lines = computeAmmsConsumptionLines({
                    batchUsages: group.batchUsages.map(({ usage, batch }) => ({
                        batchId: usage.batchId,
                        quantity: usage.quantity,
                        strength: { value: batch?.tradeName?.strengthValue, unit: batch?.tradeName?.strengthUnit?.ucumCode },
                    })),
                    weighings: group.weighings,
                    doseUnit: group.doseUnit,
                    totalPrescribedAmount: group.totalPrescribedAmount,
                });
                lines.forEach((line, lineIndex) => {
                    const batch = group.batchUsages.find(({ usage }) => usage.batchId === line.batchId)?.batch;
                    if (!batch?.rawZiv)
                        return;
                    const position = consumptionSegments.length + 1;
                    consumptionSegments.push(buildAmmsZcmSegment({
                        context,
                        positionNumber: position,
                        pznOrEan: formatAmmsPznOrEan(batch.tradeName) ?? '',
                        tradeName: batch.tradeName?.name ?? '',
                        manufacturerBatch: batch.lotNumber,
                        expiryDate: formatAmmsDate(new Date(batch.expiryDate)),
                        quantityUsed: line.quantityUsed,
                        unit: group.unit,
                        // ZCM-5: the commercial package's contents; falls back to the line's consumption
                        // (one package fully consumed) when the trade name has no strength recorded.
                        packageQuantity: computeAmmsPackageContent(batch.tradeName) ?? line.quantityUsed,
                        isFirstLine: lineIndex === 0,
                        totalPrescribedAmount: group.totalPrescribedAmount,
                    }), Hl7Segment.parse(batch.rawZiv, context));
                });
            }
            return createAmmsMessage({
                ...AMMS_MSH_DEFAULTS,
                messageType: MESSAGE_TYPES.PRESCRIPTION_VALIDATE_MESSAGE_TYPE,
                sequence: await nextAmmsControlSequence(tx),
                sentAt: new Date(now),
                segments: [
                    ...headerSegments,
                    ...buildRqdSegments(),
                    // Finished preparation: blank production number, as in the accepted legacy messages.
                    buildAmmsZivFinishedPreparationSegment({ context }),
                    ...consumptionSegments,
                ],
            });
        },
    });
}
// Administration and note fields shared by both validation saves.
function buildValidationSequenceFields(input) {
    return {
        ...(input.carrierSolutionId && { carrierSolutionId: input.carrierSolutionId }),
        ...(isDefined(input.applicationFormId) && { applicationFormId: input.applicationFormId }),
        ...(isDefined(input.duration) && { duration: input.duration }),
        ...(isDefined(input.volume) && { volume: input.volume }),
        ...(isDefined(input.volumeMode) && { volumeMode: input.volumeMode }),
        // notes are only written when provided — leaving them out must not reset them
        ...(isDefined(input.therapyNote) && { therapyNote: input.therapyNote }),
        ...(isDefined(input.pharmacyNote) && { pharmacyNote: input.pharmacyNote }),
    };
}
/**
 * Builds the medicine updates shared by both validations. The pharmacist requires every
 * medicine with a prescribed name; the doctor sends the dose fields and recomputes totals.
 */
function buildValidationMedicineUpdates(params) {
    const { tx, sequence, input, userId, now, requireAll, requirePrescribedName, recalculateTotals, trackUpdatedBy, canEditValidatedPrescription, } = params;
    const inputById = new Map(input.map(medicine => [medicine.id, medicine]));
    const characteristics = sequence.patient?.characteristics?.[0];
    const gfr = sequence.patient?.gfrs?.[0]?.gfr;
    const ops = [];
    const medicines = [];
    for (const current of sequence.medicine ?? []) {
        if (current.deletedAt != null)
            continue;
        const found = inputById.get(current.id);
        if (!found) {
            if (requireAll) {
                throw new Error(`Medicine with id ${current.id} is missing in the validation input.`);
            }
            continue;
        }
        if (requirePrescribedName && !found.prescribedNameId) {
            throw new Error(`Medicine with id ${current.id} is missing its prescribed name.`);
        }
        // No Aut Idem fixed the product the doctor validated, so only a doctor may change it
        if (current.autIdem &&
            !canEditValidatedPrescription &&
            found.prescribedNameId != null &&
            found.prescribedNameId !== current.prescribedNameId) {
            throw new Error('Only a doctor can change the trade name of a not-aut-idem prescription');
        }
        const calculationMethod = found.calculationMethod ?? current.calculationMethod ?? 'ABSOLUTE';
        const regularDose = found.regularDose ?? current.regularDose ?? 0;
        const precisionDecimals = found.precisionDecimals ?? current.precisionDecimals ?? 2;
        const doseProportion = found.doseProportion ?? current.doseProportion ?? 100;
        const activeSubstanceId = found.activeSubstanceId ?? current.activeSubstanceId;
        ops.push(() => tx.mutate.patientTherapySequenceMedicine.update({
            id: current.id,
            ...(isDefined(found.prescribedNameId) && { prescribedNameId: found.prescribedNameId }),
            ...(isDefined(found.activeSubstanceId) && { activeSubstanceId: found.activeSubstanceId }),
            ...(isDefined(found.calculationMethod) && { calculationMethod: found.calculationMethod }),
            ...(isDefined(found.regularDose) && { regularDose: found.regularDose }),
            ...(isDefined(found.precisionDecimals) && { precisionDecimals: found.precisionDecimals }),
            ...(isDefined(found.doseProportion) && { doseProportion: found.doseProportion }),
            ...(recalculateTotals && {
                totalAmount: getTotalAmountOfSubstanceDecimal({
                    calculationMethod,
                    dose: regularDose,
                    doseProportion,
                    roundingMode: activeSubstanceId ? recalculateTotals.roundingModes.get(activeSubstanceId) : undefined,
                    dosePrecision: precisionDecimals,
                    height: characteristics?.height ?? null,
                    weight: getDosingWeight(characteristics),
                    gfr: gfr ?? null,
                    bsa: characteristics?.bsa ?? null,
                }),
            }),
            ...(trackUpdatedBy && { updatedAt: now, updatedBy: userId }),
        }));
        medicines.push({
            id: current.id,
            activeSubstanceId: current.activeSubstanceId,
            prescribedNameId: found.prescribedNameId ?? current.prescribedNameId ?? '',
            totalAmount: current.totalAmount,
            calculationMethod: current.calculationMethod,
        });
    }
    return { ops, medicines };
}
const CARRIER_VALIDATION_MESSAGES = {
    carrier_solution_required: 'A carrier solution is required',
    volume_required: 'A carrier solution requires a volume greater than 0',
    volume_mode_required: 'A carrier solution requires an administration method',
};
// A carrier solution must have a positive volume and a method: imported prescriptions can miss
// either while the form requires both, and validating would release a bare carrier.
export function assertValidationCarrier(sequence, input) {
    const result = CarrierSolutionValidationSchema.safeParse({
        carrierSolutionId: input.carrierSolutionId ?? sequence.carrierSolutionId,
        volume: input.volume ?? sequence.volume,
        volumeMode: input.volumeMode ?? sequence.volumeMode,
    });
    if (!result.success) {
        const message = result.error.issues[0]?.message;
        throw new Error((message && CARRIER_VALIDATION_MESSAGES[message]) || 'Invalid carrier solution');
    }
}
/** Products the medicines would be validated with that have no active trade name left, so no stock can ever be added. */
export async function findInactiveTradeNames(tx, sequence, input) {
    const inputById = new Map(input.map(medicine => [medicine.id, medicine]));
    const prescribedNameIds = new Set((sequence.medicine ?? [])
        .filter(medicine => medicine.deletedAt == null)
        .map(medicine => inputById.get(medicine.id)?.prescribedNameId ?? medicine.prescribedNameId)
        .filter(isDefined));
    if (prescribedNameIds.size === 0)
        return [];
    const prescribedNames = await tx.run(zql.prescribedNames
        .where('id', 'IN', [...prescribedNameIds])
        .related('tradeNames', tn => tn.where('status', '=', 'active').where('deletedAt', 'IS', null)));
    return prescribedNames.filter(name => name.tradeNames.length === 0).map(name => name.name);
}
export async function pharmacistValidatePatientTherapySequence(tx, sequence, input, userId, canEditValidatedPrescription) {
    assertValidationCarrier(sequence, input);
    // checked on the server only: the browser may not hold the trade names, and must not refuse on missing data (CY-259)
    if (tx.location === 'server') {
        const inactive = await findInactiveTradeNames(tx, sequence, input.medicine);
        if (inactive.length > 0) {
            throw new Error(`${inactive.join(', ')} is inactive in the catalogue. Choose an active trade name.`);
        }
    }
    const { ops, medicines: validatedMedicine } = buildValidationMedicineUpdates({
        tx,
        sequence,
        input: input.medicine,
        userId,
        now: Date.now(),
        requireAll: true,
        requirePrescribedName: true,
        canEditValidatedPrescription,
    });
    const sequenceFields = buildValidationSequenceFields(input);
    ops.push(() => tx.mutate.patientTherapySequence.update({
        id: sequence.id,
        ...sequenceFields,
        ...(isDefined(input.preparationType) && { preparationType: input.preparationType }),
        pharmacistValidatedBy: userId,
    }));
    await Promise.all(ops.map(op => op()));
    await logAmmsOrmMessage(tx, {
        sequence: {
            id: sequence.id,
            patientId: sequence.patientId,
            internalId: sequence.internalId,
            applicationFormId: input.applicationFormId ?? sequence.applicationFormId,
            duration: input.duration ?? sequence.duration,
            therapyDate: sequence.therapyDate,
            time: sequence.time,
            createdAt: sequence.createdAt,
            doctorValidatedBy: sequence.doctorValidatedBy,
        },
        medicine: validatedMedicine,
        pharmacistUserId: userId,
        now: Date.now(),
    });
    if (tx.location !== 'server')
        return [];
    return pharmacistValidationLogItems(tx, sequence, validatedMedicine, sequenceFields);
}
/** Log lines of a pharmacist validation, with drug, trade name, carrier and application form names looked up. */
async function pharmacistValidationLogItems(tx, sequence, validatedMedicine, sequenceFields) {
    const drugIds = [
        ...validatedMedicine.map(medicine => medicine.activeSubstanceId),
        sequence.carrierSolutionId,
        sequenceFields.carrierSolutionId,
    ].filter((id) => Boolean(id));
    const tradeNameIds = validatedMedicine.map(medicine => medicine.prescribedNameId).filter(Boolean);
    const formIds = [sequence.applicationFormId, sequenceFields.applicationFormId].filter((id) => Boolean(id));
    const drugs = drugIds.length ? await tx.run(zql.drugCatalogItems.where('id', 'IN', drugIds)) : [];
    const tradeNames = tradeNameIds.length ? await tx.run(zql.prescribedNames.where('id', 'IN', tradeNameIds)) : [];
    const forms = formIds.length ? await tx.run(zql.applicationForms.where('id', 'IN', formIds)) : [];
    const drugName = (id) => nameOf(drugs)(id) ?? null;
    const formName = (id) => nameOf(forms)(id) ?? null;
    return validationItems({
        medicines: validatedMedicine.map(medicine => ({
            substance: drugName(medicine.activeSubstanceId) ?? '-',
            tradeName: nameOf(tradeNames)(medicine.prescribedNameId) ?? null,
        })),
        prev: { duration: sequence.duration, volume: sequence.volume },
        next: sequenceFields,
        carrier: sequenceFields.carrierSolutionId
            ? { old: drugName(sequence.carrierSolutionId), new: drugName(sequenceFields.carrierSolutionId) }
            : undefined,
        applicationForm: sequenceFields.applicationFormId
            ? { old: formName(sequence.applicationFormId), new: formName(sequenceFields.applicationFormId) }
            : undefined,
    });
}
async function nextLocalOrderCode(tx, prefix = 'ORD', base = 1000) {
    const prefixDash = `${prefix}-`;
    const existing = await tx.run(zql.orders.where('internalId', 'ILIKE', `${prefixDash}%`));
    const maxNum = existing
        .map(r => Number.parseInt(String(r.internalId).slice(prefixDash.length), 10))
        .filter((n) => Number.isFinite(n))
        .reduce((acc, n) => Math.max(acc, n), base) || base;
    return `${prefixDash}${maxNum + 1}`;
}
/** pickingSourceItems with the batches and trade names of both lists looked up. */
async function pickingListSourceItems(tx, initial, chosen) {
    if (!initial?.length)
        return [];
    const batchIdSet = new Set();
    for (const order of [...initial, ...chosen]) {
        for (const part of [order.activeSubstances, order.carrier, order.solvents]) {
            for (const batch of part.batches)
                batchIdSet.add(batch.batchId);
        }
        for (const restVial of order.restVials)
            if (restVial.batchId)
                batchIdSet.add(restVial.batchId);
    }
    const batchIds = [...batchIdSet];
    const batchRows = batchIds.length ? await tx.run(zql.batches.where('id', 'IN', batchIds)) : [];
    const tradeNameIds = [...new Set(batchRows.flatMap(batch => (batch.tradeNameId ? [batch.tradeNameId] : [])))];
    const tradeNameRows = tradeNameIds.length ? await tx.run(zql.tradeNames.where('id', 'IN', tradeNameIds)) : [];
    return pickingSourceItems(initial, chosen, batchRows, tradeNameRows);
}
export async function startOrderProduction(tx, input, userId) {
    const sequences = await tx.run(zql.patientTherapySequence
        .related('patient')
        .where('id', 'IN', input.sequenceIds)
        .where('status', '=', PatientTherapySequenceStatus.enum.production_planning));
    if (sequences.length !== input.sequenceIds.length) {
        throw new Error(`One or more patient therapy sequences not found or not in production planning status.`);
    }
    const productionLocation = await tx.run(zql.productionLocations.where('id', '=', input.productionLocationId).one());
    if (!productionLocation) {
        throw new Error(`Production location not found: ${input.productionLocationId}`);
    }
    const now = new Date();
    const orderId = await nextLocalOrderCode(tx, `ORD-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`);
    const orderUuid = uuidv7();
    const sequenceOps = [];
    sequenceOps.push(() => {
        return tx.mutate.orders.insert({
            id: orderUuid,
            internalId: orderId,
            productionLocationId: input.productionLocationId,
            distributedOrders: {
                ...(input.orders && { orders: input.orders }),
            },
        });
    });
    for (const sequenceId of input.sequenceIds) {
        sequenceOps.push(() => tx.mutate.patientTherapySequence.update({
            id: sequenceId,
            status: PatientTherapySequenceStatus.enum.in_production,
            ...(input.dispatchMode === DispatchMode.enum['smart-compounder'] && { compoundedByMachine: true }),
            updatedAt: now.getTime(),
            updatedBy: userId,
            orderId: orderUuid,
        }));
        if (tx.location === 'server') {
            const sequence = sequences.find(s => s.id === sequenceId);
            if (sequence) {
                sequenceOps.push(() => addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.patientTherapySequence,
                    resourceId: sequenceId,
                    action: 'update',
                    domain: 'production',
                    event: EventLogs.PRODUCTION_STARTED,
                    patches: createPatch(sequence, {
                        id: sequence.id,
                        status: PatientTherapySequenceStatus.enum.in_production,
                        ...(input.dispatchMode === DispatchMode.enum['smart-compounder'] && { compoundedByMachine: true }),
                        updatedAt: now.getTime(),
                        updatedBy: userId,
                        orderId: orderUuid,
                    }),
                    performedBy: userId,
                    performedAt: now.getTime(),
                    status: 'INFO',
                    details: {
                        therapyInternalId: sequence.internalId ?? '',
                        patientName: sequence.patient
                            ? [sequence.patient.givenName, sequence.patient.familyName].filter(Boolean).join(' ')
                            : undefined,
                        patientId: sequence.patientId,
                        orderInternalId: orderId,
                    },
                }));
            }
        }
    }
    if (tx.location === 'server') {
        const patches = createPatch(input.initialOrders ?? [], input.orders);
        const [firstSequence] = sequences;
        sequenceOps.push(async () => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.order,
            resourceId: orderUuid,
            action: 'create',
            domain: 'production',
            event: EventLogs.ORDER_PRODUCTION_STARTED,
            patches,
            performedBy: userId,
            performedAt: now.getTime(),
            status: 'INFO',
            details: {
                orderInternalId: orderId,
                productionLocationId: input.productionLocationId,
                prescriptionId: firstSequence?.internalId,
                patientName: firstSequence?.patient
                    ? [firstSequence.patient.givenName, firstSequence.patient.familyName].filter(Boolean).join(' ')
                    : undefined,
                patientId: firstSequence?.patientId,
                ...itemsDetail([
                    ...valueItem('prescriptions', input.sequenceIds.length),
                    ...(await pickingListSourceItems(tx, input.initialOrders, input.orders)),
                ]),
            },
        }));
    }
    if (input.dispatchMode === DispatchMode.enum['smart-compounder']) {
        for (const sequence of sequences) {
            sequenceOps.push(() => tx.mutate.compoundOrders.insert({
                id: uuidv7(),
                externalId: sequence.internalId ?? sequence.id,
                therapySequenceId: sequence.id,
                orderId: orderUuid,
                status: CompoundOrderStatus.enum.PENDING,
                createdAt: now.getTime(),
                createdBy: userId,
            }));
        }
    }
    const uniqueBatchIds = Array.from(new Set(input.usedBatches));
    // one row per batch and prescription, so the drawer and reports show only the lots that prescription used (CY-382)
    for (const row of getSequenceBatchRows(input.orders)) {
        sequenceOps.push(() => tx.mutate.orderBatches.insert({
            id: uuidv7(),
            orderId: orderUuid,
            batchId: row.batchId,
            therapySequenceId: row.sequenceId,
            category: row.category,
        }));
    }
    const batches = await tx.run(zql.batches.where('id', 'IN', uniqueBatchIds));
    const { usedBatches, usedBatchesBySequence, mappedRestVials, mappedReturns } = getAggregatedBatchInfo(input.orders, batches);
    // the picking list is a snapshot: a suggested rest vial may have been discarded, used or
    // allocated to another production before this one was started (server-only: the client cache may not hold them)
    if (tx.location === 'server' && mappedRestVials.length > 0) {
        await lockRestVials(tx);
        const restVialIds = mappedRestVials.map(rv => rv.restVialId);
        const usable = await tx.run(zql.restVials
            .where('id', 'IN', restVialIds)
            .where('deletedAt', 'IS', null)
            .where('status', '=', RestVialStatus.enum.AVAILABLE));
        const usableIds = new Set(usable.filter(rv => rv.expiryDate == null || rv.expiryDate >= now.getTime()).map(rv => rv.id));
        const unavailable = restVialIds.filter(id => !usableIds.has(id));
        if (unavailable.length > 0) {
            throw new Error(`${unavailable.length} allocated rest vial(s) are no longer available (discarded, used or expired). Regenerate the picking list.`);
        }
    }
    // same for a suggested return: it may have expired since the picking list was generated (CY-328)
    if (tx.location === 'server' && mappedReturns.length > 0) {
        const returnIds = mappedReturns.map(ret => ret.returnId);
        const usable = await tx.run(zql.returns
            .where('id', 'IN', returnIds)
            .where('deletedAt', 'IS', null)
            .where('status', '=', ReturnStatus.enum.AVAILABLE));
        const usableIds = new Set(usable.filter(ret => !isReturnExpired(ret.expiryDate, now.getTime())).map(ret => ret.id));
        const unavailable = returnIds.filter(id => !usableIds.has(id));
        if (unavailable.length > 0) {
            throw new Error(`${unavailable.length} allocated return(s) are no longer available (discarded, used or expired). Regenerate the picking list.`);
        }
    }
    // the picking list carries the trade name of each rest vial
    const restVialNames = new Map(input.orders.flatMap(order => order.restVials.map(restVial => [restVial.restVialId, restVial.tradeNameName])));
    for (const restVial of mappedRestVials) {
        sequenceOps.push(() => tx.mutate.restVials.update({
            id: restVial.restVialId,
            status: RestVialStatus.enum.IN_USE,
            updatedAt: now.getTime(),
            updatedBy: userId,
        }));
        if (tx.location === 'server') {
            sequenceOps.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.restVial,
                resourceId: restVial.restVialId,
                action: 'update',
                event: EventLogs.REST_VIAL_IN_USE,
                patches: createPatch(restVial, { status: RestVialStatus.enum.IN_USE }),
                performedBy: userId,
                performedAt: now.getTime(),
                domain: 'production',
                details: {
                    orderInternalId: orderId,
                    orderId: orderUuid,
                    itemName: restVialNames.get(restVial.restVialId) ?? '',
                },
            }));
        }
    }
    for (const usedReturn of mappedReturns) {
        sequenceOps.push(() => tx.mutate.returns.update({
            id: usedReturn.returnId,
            status: ReturnStatus.enum.IN_USE,
            updatedAt: now.getTime(),
            updatedBy: userId,
        }));
        if (tx.location === 'server') {
            sequenceOps.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.return,
                resourceId: usedReturn.returnId,
                action: 'update',
                event: EventLogs.RETURN_IN_USE,
                patches: createPatch(usedReturn, { status: ReturnStatus.enum.IN_USE }),
                performedBy: userId,
                performedAt: now.getTime(),
                domain: 'production',
                details: {
                    orderInternalId: orderId,
                    orderId: orderUuid,
                    medicationName: usedReturn.medicationName ?? '',
                },
            }));
        }
    }
    for (const usedBatch of usedBatches) {
        const batch = batches.find(b => b.id === usedBatch.batchId);
        if (!batch || !batch.quantity) {
            throw new Error(`Batch not found or has no quantity: ${usedBatch.batchId}`);
        }
        const batchQuantity = new Decimal(batch.quantity);
        if (batchQuantity.lessThan(usedBatch.quantity)) {
            throw new Error(`Batch ${batch.id} does not have enough quantity. Available: ${batch.quantity}, required: ${usedBatch.quantity}`);
        }
        const beforeQuantity = batchQuantity.toNumber();
        const afterQuantity = batchQuantity.minus(usedBatch.quantity).toNumber();
        sequenceOps.push(() => tx.mutate.batches.update({ id: usedBatch.batchId, quantity: afterQuantity }));
        // one movement per prescription so reports filter by patient; together they equal the batch change (CY-370)
        for (const share of usedBatchesBySequence.filter(used => used.batchId === usedBatch.batchId)) {
            sequenceOps.push(() => recordStockMovement(tx, { user: { id: userId } }, {
                type: StockMovementType.enum.consumed,
                batchId: share.batchId,
                quantity: -share.quantity,
                source: StockMovementSource.enum.new_pack,
                sequenceId: share.sequenceId,
                orderId: orderUuid,
            }, now.getTime()));
        }
        if (tx.location === 'server') {
            sequenceOps.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.batch,
                resourceId: usedBatch.batchId,
                action: 'update',
                patches: createPatch({ quantity: beforeQuantity }, { quantity: afterQuantity }),
                performedBy: userId,
                performedAt: now.getTime(),
                domain: 'production',
                event: EventLogs.BATCH_CONSUMED,
                details: {
                    orderInternalId: orderId,
                    orderId: orderUuid,
                    usedQuantity: String(usedBatch.quantity),
                    lotNumber: batch.lotNumber ?? '',
                    ...itemsDetail(changedItems({ quantity: beforeQuantity }, { quantity: afterQuantity }, [{ field: 'quantity' }])),
                },
            }));
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
}
const ORDER_BATCH_CATEGORY_BY_TYPE = [
    ['activeSubstances', OrderBatchCategory.enum.ACTIVE_SUBSTANCE],
    ['carrier', OrderBatchCategory.enum.CARRIER],
    ['solvents', OrderBatchCategory.enum.SOLVENT],
    ['consumables', OrderBatchCategory.enum.CONSUMABLE],
];
/**
 * Flattens the picking list into the batches each prescription used. The same batch can appear
 * under several roles (a carrier also used as a solvent), so the role is part of the identity.
 */
export function getSequenceBatchRows(distributedList) {
    const rows = new Map();
    const add = (sequenceId, batchId, category) => {
        if (!batchId)
            return;
        rows.set(`${sequenceId}:${batchId}:${category}`, { sequenceId, batchId, category });
    };
    for (const order of distributedList) {
        for (const [type, category] of ORDER_BATCH_CATEGORY_BY_TYPE) {
            for (const batch of order[type].batches)
                add(order.sequenceId, batch.batchId, category);
        }
        // a rest vial is a partly used drug vial, so it counts as an active-substance lot
        for (const restVial of order.restVials)
            add(order.sequenceId, restVial.batchId, OrderBatchCategory.enum.ACTIVE_SUBSTANCE);
    }
    return [...rows.values()];
}
const batchTypes = ['activeSubstances', 'carrier', 'solvents'];
/**
 * Returns:
 * - mappedBatches: detailed batch rows (with sharing info)
 * - errors: shortages
 * - usedBatches: one {batchId, quantity} per batch, summed over shared and full rows (CY-370)
 * - usedBatchesBySequence: each prescription's share of a batch; a shared vial is split equally
 *   between the rows of its group, and the shares of a batch add up to its usedBatches quantity
 */
export function getAggregatedBatchInfo(distributedList, batches) {
    const mappedBatches = [];
    // keyed by batch and prescription: summed per batch for the stock update, kept per prescription for the movements (CY-370)
    const usedBySequence = new Map();
    const addUsed = (batchId, sequenceId, quantity) => {
        const key = `${batchId}:${sequenceId}`;
        const used = usedBySequence.get(key) ?? { batchId, sequenceId, quantity: new Decimal(0) };
        usedBySequence.set(key, { ...used, quantity: used.quantity.plus(quantity) });
    };
    for (const type of batchTypes) {
        const rows = Object.groupBy(distributedList.flatMap(order => {
            return order[type].batches.map(batch => ({ ...batch, orderId: order.orderId, sequenceId: order.sequenceId }));
        }), batch => batch.batchId);
        for (const [batchId, batchRows] of Object.entries(rows)) {
            if (!batchRows)
                continue;
            const batchInfo = batches.find(b => b.id === batchId);
            if (!batchInfo)
                continue;
            // Shared vials
            const sharedGroups = Object.groupBy(batchRows.filter(b => b.sharedWith.length > 0), b => `${b.batchId}:${[...b.sharedWith].toSorted().join(',')}`);
            for (const groupRows of Object.values(sharedGroups)) {
                const [b] = groupRows ?? [];
                if (!groupRows || !b)
                    continue;
                mappedBatches.push({
                    batchId: b.batchId,
                    quantity: b.quantity,
                    sharedWith: b.sharedWith,
                    batchInfo,
                    type,
                });
                // equal shares to 2 decimals; the last row takes the remainder so the group adds up to the vial
                const share = new Decimal(b.quantity).dividedBy(groupRows.length).toDecimalPlaces(2);
                groupRows.forEach((row, index) => {
                    const isLast = index === groupRows.length - 1;
                    addUsed(row.batchId, row.sequenceId, isLast ? share.times(-index).plus(b.quantity) : share);
                });
            }
            // Full batches
            const fullBatches = batchRows.filter(b => b.sharedWith.length === 0);
            const totalFullQuantity = fullBatches.reduce((sum, b) => sum + b.quantity, 0);
            const ordersSharingThisBatch = new Set();
            for (const batch of fullBatches) {
                ordersSharingThisBatch.add(batch.orderId);
            }
            if (totalFullQuantity > 0) {
                mappedBatches.push({
                    batchId,
                    quantity: totalFullQuantity,
                    sharedWith: [],
                    ordersSharingThisBatch: Array.from(ordersSharingThisBatch),
                    batchInfo,
                    type,
                });
                for (const batch of fullBatches)
                    addUsed(batchId, batch.sequenceId, batch.quantity);
            }
        }
    }
    // Consumables
    const consumableRows = Object.groupBy(distributedList.flatMap(order => {
        return order.consumables.batches.map(batch => ({ ...batch, orderId: order.orderId, sequenceId: order.sequenceId }));
    }), batch => batch.batchId);
    for (const [batchId, batchRows] of Object.entries(consumableRows)) {
        if (!batchRows)
            continue;
        const batchInfo = batches.find(b => b.id === batchId);
        if (!batchInfo)
            continue;
        const totalQuantity = batchRows.reduce((sum, b) => sum + b.quantity, 0);
        mappedBatches.push({
            batchId,
            quantity: totalQuantity,
            sharedWith: [],
            ordersSharingThisBatch: batchRows.map(b => b.orderId),
            batchInfo,
            type: 'consumables',
        });
        for (const batch of batchRows)
            addUsed(batchId, batch.sequenceId, batch.quantity);
    }
    // Rest vials — deduplicated by restVialId
    const seenRestVialIds = new Set();
    const mappedRestVials = [];
    for (const order of distributedList) {
        if (order?.restVials) {
            for (const rv of order.restVials) {
                if (seenRestVialIds.has(rv.restVialId))
                    continue;
                seenRestVialIds.add(rv.restVialId);
                if (!rv.batchId || rv.amount == null)
                    continue;
                const batchInfo = batches.find(b => b.id === rv.batchId);
                if (!batchInfo)
                    continue;
                mappedRestVials.push({
                    restVialId: rv.restVialId,
                    amount: Number(rv.amount),
                    batchId: rv.batchId,
                    expiryDate: rv.expiryDate,
                    strengthUnit: rv.strengthUnit ?? '',
                    batchInfo,
                    ordersSharingThisBatch: [order.orderId],
                });
            }
        }
    }
    // Returns — deduplicated by returnId
    const seenReturnIds = new Set();
    const mappedReturns = [];
    for (const order of distributedList) {
        if (order?.returns) {
            for (const ret of order.returns) {
                if (seenReturnIds.has(ret.returnId))
                    continue;
                seenReturnIds.add(ret.returnId);
                mappedReturns.push(ret);
            }
        }
    }
    // Errors
    const requiredByBatchId = new Map();
    for (const row of mappedBatches) {
        requiredByBatchId.set(row.batchId, (requiredByBatchId.get(row.batchId) ?? 0) + row.quantity);
    }
    const errors = [];
    for (const [batchId, requiredQuantity] of requiredByBatchId.entries()) {
        const batchInfo = batches.find(b => b.id === batchId);
        if (!batchInfo)
            continue;
        const availableQuantity = batchInfo.quantity ?? 0;
        if (requiredQuantity > availableQuantity) {
            errors.push({
                batchId,
                requiredQuantity,
                availableQuantity,
                missingQuantity: requiredQuantity - availableQuantity,
                batchInfo,
            });
        }
    }
    const usedBatchesBySequence = [];
    const usedByBatchId = new Map();
    for (const { batchId, sequenceId, quantity } of usedBySequence.values()) {
        usedBatchesBySequence.push({ batchId, sequenceId, quantity: quantity.toNumber() });
        usedByBatchId.set(batchId, (usedByBatchId.get(batchId) ?? new Decimal(0)).plus(quantity));
    }
    const usedBatches = Array.from(usedByBatchId, ([batchId, quantity]) => ({
        batchId,
        quantity: quantity.toNumber(),
    }));
    return { mappedBatches, mappedRestVials, mappedReturns, errors, usedBatches, usedBatchesBySequence };
}
//# sourceMappingURL=state-machine.js.map